import api from './api';

interface PushSubscriptionPayload {
  subscription: PushSubscriptionJSON;
  userAgent?: string;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

// Storage key for user's subscription preference
const PUSH_SUBSCRIPTION_PREFERENCE_KEY = 'pushNotificationPreference';

class PushNotificationService {
  private permission: NotificationPermission = 'default';
  private swRegistration: ServiceWorkerRegistration | null = null;
  private vapidPublicKey: string | null = null;
  private currentSubscription: PushSubscription | null = null;

  async init(): Promise<void> {
    // Check if notifications and push are supported
    if (!('Notification' in window) || !('PushManager' in window)) {
      console.warn('Push notifications are not supported');
      return;
    }

    // Check current permission
    this.permission = Notification.permission;

    // Get service worker registration
    if ('serviceWorker' in navigator) {
      try {
        this.swRegistration = await navigator.serviceWorker.ready;

        // Get existing subscription
        this.currentSubscription =
          await this.swRegistration.pushManager.getSubscription();

        // Fetch VAPID public key from backend
        await this.fetchVapidKey();

        // Check user preference from localStorage
        const userPreference = localStorage.getItem(
          PUSH_SUBSCRIPTION_PREFERENCE_KEY,
        );

        // Only auto-subscribe if:
        // 1. User has granted permission
        // 2. There's no current subscription
        // 3. User hasn't explicitly opted out (userPreference !== 'disabled')
        // 4. We have a VAPID key
        if (
          this.permission === 'granted' &&
          !this.currentSubscription &&
          this.vapidPublicKey &&
          userPreference !== 'disabled'
        ) {
          // Only subscribe if user previously enabled it or it's their first time
          if (userPreference === 'enabled') {
            await this.subscribe();
          }
        }
      } catch (error) {
        console.error('Service Worker not ready:', error);
      }
    }
  }

  private async fetchVapidKey(): Promise<void> {
    try {
      const response = await api.pushGetVapidKey();
      this.vapidPublicKey = response.publicKey;
    } catch (error) {
      console.error('Failed to fetch VAPID public key:', error);
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'denied';
    }

    this.permission = await Notification.requestPermission();

    if (this.permission === 'granted') {
      // Store user preference as enabled
      localStorage.setItem(PUSH_SUBSCRIPTION_PREFERENCE_KEY, 'enabled');
      await this.subscribe();
    }

    return this.permission;
  }

  private async subscribe(): Promise<void> {
    if (!this.swRegistration || !this.vapidPublicKey) {
      console.warn('Cannot subscribe: missing registration or VAPID key');
      return;
    }

    try {
      // Subscribe to push notifications
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(this.vapidPublicKey),
      });

      // Send subscription to backend
      await api.pushSubscribe({
        subscription: subscription.toJSON(),
        userAgent: navigator.userAgent,
      });

      this.currentSubscription = subscription;
      console.log('Successfully subscribed to push notifications');
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
    }
  }

  async unsubscribe(): Promise<void> {
    if (!this.currentSubscription) {
      console.warn('No active subscription to unsubscribe');
      return;
    }

    try {
      // Unsubscribe from push manager
      await this.currentSubscription.unsubscribe();

      // Notify backend
      const { endpoint } = this.currentSubscription;
      await api.pushUnsubscribe({ endpoint });

      this.currentSubscription = null;

      // Store user preference as disabled
      localStorage.setItem(PUSH_SUBSCRIPTION_PREFERENCE_KEY, 'disabled');

      console.log('Successfully unsubscribed from push notifications');
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
    }
  }

  getPermissionStatus(): NotificationPermission {
    return this.permission;
  }

  isSubscribed(): boolean {
    return !!this.currentSubscription;
  }

  // Fallback local notification (for testing or when push fails)
  async showLocalNotification(
    title: string,
    options?: NotificationOptions,
  ): Promise<void> {
    if (this.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    try {
      if (this.swRegistration) {
        await this.swRegistration.showNotification(title, options);
      } else {
        new Notification(title, options);
      }
    } catch (error) {
      console.error('Failed to show notification:', error);
    }
  }
}

export const pushNotificationService = new PushNotificationService();

export type { PushSubscriptionPayload };
